"use client";
import useTranslate from "@app/_hooks/useTranslate";

import Link from "@components/Link";
import { Disclosure } from "@headlessui/react";
import { ArrowIcon, MinusIcon, PlusIcon } from "@ngg/icons";
import type {
  ExceptionalOpeningHoursDay,
  LayoutStoreFragment,
  OpeningHoursDay,
} from "@server/gql/graphql";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import { cn } from "@/lib/utils";
import {
  getIrregularOpeningHoursDisplayData,
  getOpeningHoursDisplayData,
} from "@/utils/common";

export default function FooterStore({
  className,
  data,
  locale,
}: {
  className?: string;
  data: LayoutStoreFragment;
  locale: string | undefined;
}) {
  const t = useTranslate();
  const openingHoursData = data?.openingHoursCollection
    ?.items as OpeningHoursDay[];
  const irregularOpeningHours = data?.exceptionalOpeningHoursCollection
    ?.items as ExceptionalOpeningHoursDay[] | undefined;

  const irregularDisplayData = irregularOpeningHours
    ? getIrregularOpeningHoursDisplayData(irregularOpeningHours, t)
    : null;
  const displayData = getOpeningHoursDisplayData(openingHoursData);

  return (
    <Disclosure
      defaultOpen={data?.type === "Online"}
      as="div"
      className={className}
    >
      {({ open }) => (
        <>
          <h3 className="text-base">
            <Disclosure.Button className="all-small-caps flex w-full items-center justify-between px-5 py-4 font-normal uppercase tracking-wider md:hidden">
              {data?.title}
              <span className="md:hidden">
                {open && <MinusIcon className="size-3" />}
                {!open && <PlusIcon className="size-3" />}
              </span>
            </Disclosure.Button>

            <span className="all-small-caps mb-4 hidden font-normal uppercase tracking-wider md:block">
              {data?.title}
            </span>
          </h3>

          <Disclosure.Panel
            static
            className={cn("max-md:px-5", open ? "block" : "hidden md:block")}
          >
            <div>
              {data?.infoText ? <p>{data.infoText}</p> : null}
              {data?.type === "Store" ? (
                <div>
                  {data?.address ? <p>{data.address}</p> : null}
                  {data?.postalCode && data.city ? (
                    <p>{`${data.postalCode} ${data.city}`}</p>
                  ) : null}
                  {data?.storePageLinkText && data?.storePage?.slug ? (
                    <Link
                      locale={locale}
                      className="group inline-flex items-center underline"
                      href={data.storePage.slug}
                    >
                      {data?.storePageLinkText}
                      <ArrowIcon className="size-3 text-black opacity-0 transition-[opacity,transform] group-hover:translate-x-0.5 group-hover:opacity-100 supports-[hover]:opacity-0" />
                    </Link>
                  ) : null}
                </div>
              ) : null}
              <div className="mb-6">
                {data?.type === "Online" &&
                FEATURE_FLAGS.customerService.emailLink &&
                data?.email ? (
                  <p>
                    <a
                      className="hover:underline"
                      href={`mailto:${data?.email}`}
                    >
                      {data.email}
                    </a>
                  </p>
                ) : null}
              </div>
              {data?.type === "Online" &&
                data?.storePage?.slug &&
                (t("contactForm", { strict: true }) ||
                  data?.storePageLinkText) && (
                  <div className="mb-6">
                    <Link
                      locale={locale}
                      className="all-small-caps group inline-flex items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap border border-color-base px-4 py-2 uppercase tracking-wider transition-colors hover:bg-black hover:text-white"
                      href={data?.storePage?.slug}
                    >
                      {data?.storePageLinkText}
                      <ArrowIcon className="text-lg text-current" />
                    </Link>
                  </div>
                )}
              <div className="mb-6">
                {data.type === "Online" ? (
                  <>
                    <h3 className="all-small-caps mb-3 font-normal uppercase tracking-wider">
                      {t("regularOpeningHours")}
                    </h3>
                    {displayData?.map((item, index) => (
                      <p key={item?.text + index}>
                        {item?.text}
                        {": "}
                        {item?.time}
                      </p>
                    ))}
                  </>
                ) : (
                  <>
                    <h3 className="all-small-caps mb-3 font-normal uppercase tracking-wider">
                      {t("openingHours")}
                    </h3>
                    {displayData?.map((item, index) => (
                      <p key={item?.text + index}>
                        {item?.text}
                        {": "}
                        {item?.time}
                      </p>
                    ))}
                    {irregularDisplayData?.map((item, index) => (
                      <p key={item?.text + index}>
                        {item?.text}
                        {": "}
                        {item?.time}
                      </p>
                    ))}
                  </>
                )}
              </div>
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
